import type {
  AgentBuilderChartDataPoint,
  AgentBuilderKpiCard,
  DashboardFilter,
  DashboardWidget,
  DashboardWidgetType,
  DaxGenerationRequest,
  DaxGenerationRequestFilter,
  DatePresetOption,
} from './types'
import { normalizeFieldFormatRecord, type FieldFormat } from '@/lib/fieldFormat'
import { isDatePresetFilter } from './runtime'

// No HTTP here: the Claude viewer bundle imports this, and must not pull in the app's client or settings.

export type Raw = Record<string, unknown>

function normalizeResponseKey(value: string) {
  return value.trim().toLowerCase().replace(/[^a-z0-9]/g, '')
}

export function getRawValue(payload: Raw, ...keys: string[]) {
  for (const key of keys) {
    if (key in payload) return payload[key]
  }

  const entries = Object.entries(payload)
  for (const key of keys) {
    const normalizedKey = normalizeResponseKey(key)
    const match = entries.find(([entryKey]) => normalizeResponseKey(entryKey) === normalizedKey)
    if (match) return match[1]
  }

  return undefined
}

export function hasRawValue(payload: Raw, ...keys: string[]) {
  for (const key of keys) {
    if (key in payload) return true
  }

  const normalizedKeys = new Set(keys.map((key) => normalizeResponseKey(key)))
  return Object.keys(payload).some((entryKey) => normalizedKeys.has(normalizeResponseKey(entryKey)))
}

export function asString(value: unknown) {
  return typeof value === 'string' ? value : ''
}

export function asOptionalString(...values: unknown[]) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value
  }
  return undefined
}

export function asNumber(value: unknown) {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : 0
}

export function asOptionalNumber(...values: unknown[]) {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue
    const n = typeof value === 'number' ? value : Number(value)
    if (Number.isFinite(n)) return n
  }
  return undefined
}

export function asNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

export function asObject(value: unknown): Raw | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Raw) : null
}

export function asArray<T = unknown>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

export function asRowArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter((item): item is Record<string, unknown> => Boolean(asObject(item))) : []
}

export function mapChartData(value: unknown): AgentBuilderChartDataPoint[] {
  return asArray<Raw>(value).map((item) => ({
    label: asString(item['label'] ?? item['Label']),
    value: asNumber(item['value'] ?? item['Value']),
    series: asArray<Raw>(item['series'] ?? item['Series']).map((series) => ({
      name: asString(series['name'] ?? series['Name']),
      value: asNumber(series['value'] ?? series['Value']),
    })),
  }))
}

export function mapKpiCards(value: unknown): AgentBuilderKpiCard[] {
  return asArray<Raw>(value).map((item) => ({
    label: asString(item['label'] ?? item['Label']),
    value: (item['value'] ?? item['Value']) as string | number,
    measureLabel: asString(item['measureLabel'] ?? item['MeasureLabel']) || undefined,
    unit: asString(item['unit'] ?? item['Unit']) || undefined,
  }))
}

function asNumberRecord(value: unknown): Record<string, number> | null {
  const raw = asObject(value)
  if (!raw) return null
  return Object.fromEntries(
    Object.entries(raw)
      .map(([key, item]) => [key, asNumber(item)] as const)
      .filter(([, item]) => Number.isFinite(item)),
  )
}

function normalizeSelectedFilterValue(value: string | string[] | null | undefined): string | string[] | null {
  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => String(item ?? '').trim())
      .filter((item) => item && item.toLowerCase() !== 'all')
    return normalized.length > 0 ? normalized : null
  }

  const normalized = String(value ?? '').trim()
  if (!normalized) return null
  if (normalized.toLowerCase() === 'all') return null
  return normalized
}

export function dashboardFilterToDaxFilter(filter: DashboardFilter, selectedValue?: string | string[] | null): DaxGenerationRequestFilter | null {
  const value = normalizeSelectedFilterValue(selectedValue)
  if (!value) return null
  return {
    Type: filter.type,
    Field: filter.value,
    Value: value,
    Filter: Array.isArray(value) ? 'in' : '==',
  }
}

function normalizeFieldName(value: string) {
  return String(value ?? '').trim().toLowerCase()
}

export type HydratedWidgetResult = {
  rows: Record<string, unknown>[]
  columns: string[]
  chartData: AgentBuilderChartDataPoint[]
  comparisonChartData: AgentBuilderChartDataPoint[]
  kpiCards: AgentBuilderKpiCard[]
  daxQuery: string | null
  retrievedAt: string | null
  hasPaginationMetadata: boolean
  totalRows: number
  page: number
  pageSize: number
  sortColumn: string | null
  sortDirection: 'asc' | 'desc'
  groupByDimension: string | null
  groupByDimensions?: string[] | null
  axisMinValue: number | null
  axisMaxValue: number | null
  axisMinByMeasure: Record<string, number> | null
  axisMaxByMeasure: Record<string, number> | null
  columnFormats: Record<string, FieldFormat>
}

type HydratedWidgetFallbackState = Partial<Pick<
  HydratedWidgetResult,
  'totalRows' | 'page' | 'pageSize' | 'sortColumn' | 'sortDirection' | 'groupByDimension' | 'groupByDimensions'
>>

export type AgentBuilderComparisonPeriodInput = {
  enabled: boolean
  type: 'previousYear'
  label: string
}

export type HydrateWidgetInput = {
  clientId: string
  platformTypeId: number
  dashboardId?: string | null
  widget: DashboardWidget
  effectiveDaxGenerationRequest?: DaxGenerationRequest
  dashboardFilters?: DashboardFilter[]
  selectedFilters?: Record<string, string | string[]>
  page?: number
  pageSize?: number
  sortColumn?: string | null
  sortDirection?: 'asc' | 'desc'
  groupByDimension?: string | null
  groupByDimensions?: string[] | null
  comparisonPeriods?: AgentBuilderComparisonPeriodInput[]
  applyPreviewRowLimit?: boolean
  fallbackState?: HydratedWidgetFallbackState
}

export type DrilldownWidgetInput = HydrateWidgetInput & {
  field: string
  value: string
  dimension: string
  parentFilters?: Array<{ field: string; value: string }>
  /** Resolved widget drill order; only set in override mode (backend: DrillDownDimensions). */
  drillDownDimensions?: string[]
}

export type ExportWidgetDataInput = HydrateWidgetInput & {
  widgetType?: DashboardWidgetType
  field?: string | null
  value?: string | null
  dimension?: string | null
}

export type FetchSlicerOptionsInput = {
  clientId: string
  dashboardId: string | null
  platformTypeId: number
  field: string
  filters: DaxGenerationRequest['filters']
}

function widgetFilters(input: HydrateWidgetInput) {
  return (input.dashboardFilters ?? [])
    .map((filter) => dashboardFilterToDaxFilter(filter, input.selectedFilters?.[filter.value] ?? input.selectedFilters?.[filter.name]))
    .filter(Boolean)
}

/** The body of `agentBuilder/ui/widget/hydrate`, and of the connector's `hydrate_widget` tool. */
export function hydrateWidgetRequestBody(input: HydrateWidgetInput) {
  return {
    clientId: input.clientId,
    agentBuilderDashboardRootId: input.dashboardId || undefined,
    platformTypeId: input.platformTypeId,
    widgetId: input.widget.id,
    title: input.widget.title,
    widgetType: input.widget.type,
    daxGenerationRequest: input.effectiveDaxGenerationRequest ?? input.widget.daxGenerationRequest,
    dashboardFilters: widgetFilters(input),
    page: input.page,
    pageSize: input.pageSize,
    sortColumn: input.sortColumn,
    sortDirection: input.sortDirection,
    groupByDimension: input.groupByDimension,
    groupByDimensions: input.groupByDimensions,
    comparisonPeriods: input.comparisonPeriods,
    applyPreviewRowLimit: input.applyPreviewRowLimit ?? true,
  }
}

export function drilldownWidgetRequestBody(input: DrilldownWidgetInput) {
  const { comparisonPeriods: _comparisonPeriods, ...body } = hydrateWidgetRequestBody(input)
  return {
    ...body,
    field: input.field,
    value: input.value,
    dimension: input.dimension,
    parentFilters: input.parentFilters,
    drillDownDimensions: input.drillDownDimensions?.length ? input.drillDownDimensions : undefined,
  }
}

export function exportWidgetRequestBody(input: ExportWidgetDataInput) {
  const { comparisonPeriods: _comparisonPeriods, ...body } = hydrateWidgetRequestBody(input)
  return {
    ...body,
    widgetType: input.widgetType ?? input.widget.type,
    page: 1,
    applyPreviewRowLimit: false,
    field: input.field || undefined,
    value: input.value || undefined,
    dimension: input.dimension || undefined,
  }
}

/** Reads a hydrate or drilldown response; values the response leaves out fall back to what was asked for. */
export function toHydratedWidgetResult(
  response: Raw,
  requested: HydrateWidgetInput,
  { withComparison }: { withComparison: boolean },
): HydratedWidgetResult {
  const { page, pageSize, sortColumn, sortDirection, groupByDimension, groupByDimensions, fallbackState } = requested
  const rows = asRowArray(getRawValue(response, 'rows'))
  const hasPaginationMetadata = hasRawValue(response, 'totalRows') && hasRawValue(response, 'page') && hasRawValue(response, 'pageSize')
  const resolvedPage = asOptionalNumber(getRawValue(response, 'page')) ?? page ?? fallbackState?.page ?? 1
  const resolvedPageSize = asOptionalNumber(getRawValue(response, 'pageSize')) ?? pageSize ?? fallbackState?.pageSize ?? 20
  const resolvedSortColumn = asOptionalString(getRawValue(response, 'sortColumn')) ?? sortColumn ?? fallbackState?.sortColumn ?? null
  const resolvedSortDirection = (asOptionalString(getRawValue(response, 'sortDirection')) ?? sortDirection ?? fallbackState?.sortDirection ?? 'desc') === 'asc' ? 'asc' : 'desc'
  const resolvedGroupByDimension = asOptionalString(getRawValue(response, 'groupByDimension')) ?? groupByDimension ?? fallbackState?.groupByDimension ?? null
  const resolvedGroupByDimensions = asArray<string>(getRawValue(response, 'groupByDimensions'))
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
  const finalGroupByDimensions = resolvedGroupByDimensions.length > 0
    ? resolvedGroupByDimensions
    : groupByDimensions ?? fallbackState?.groupByDimensions ?? (resolvedGroupByDimension ? [resolvedGroupByDimension] : null)

  return {
    rows,
    columns: asArray<string>(getRawValue(response, 'columns')).filter((item): item is string => typeof item === 'string'),
    chartData: mapChartData(getRawValue(response, 'chartData')),
    comparisonChartData: withComparison ? mapChartData(getRawValue(response, 'comparisonChartData')) : [],
    kpiCards: mapKpiCards(getRawValue(response, 'kpiCards')),
    daxQuery: asString(getRawValue(response, 'daxQuery')) || null,
    retrievedAt: asString(getRawValue(response, 'retrievedAt')) || null,
    hasPaginationMetadata,
    totalRows: hasPaginationMetadata
      ? (asOptionalNumber(getRawValue(response, 'totalRows')) ?? fallbackState?.totalRows ?? rows.length)
      : rows.length,
    page: resolvedPage,
    pageSize: resolvedPageSize,
    sortColumn: resolvedSortColumn,
    sortDirection: resolvedSortDirection,
    groupByDimension: resolvedGroupByDimension,
    groupByDimensions: finalGroupByDimensions,
    axisMinValue: asNullableNumber(getRawValue(response, 'axisMinValue')),
    axisMaxValue: asNullableNumber(getRawValue(response, 'axisMaxValue')),
    axisMinByMeasure: asNumberRecord(getRawValue(response, 'axisMinByMeasure')),
    axisMaxByMeasure: asNumberRecord(getRawValue(response, 'axisMaxByMeasure')),
    columnFormats: normalizeFieldFormatRecord(getRawValue(response, 'columnFormats')),
  }
}

// A slicer's option list is scoped by the other slicers' values only: its own
// filter (and the date range, whether custom dates or a datePreset) is dropped
// so applying Region=West still lists every region.
export function buildSlicerOptionsRequestFilters(field: string, filters: DaxGenerationRequest['filters']) {
  return (filters ?? []).filter((filter) => (
    normalizeFieldName(filter.Field) !== normalizeFieldName(field)
      && String(filter.Type ?? '').trim().toLowerCase() !== 'date'
      && !isDatePresetFilter(filter)
  ))
}

/** The body of `agentBuilder/ui/slicerOptions`. */
export function slicerOptionsRequestBody(input: FetchSlicerOptionsInput) {
  return {
    clientId: input.clientId,
    agentBuilderDashboardRootId: input.dashboardId || undefined,
    platformTypeId: input.platformTypeId,
    field: input.field,
    filters: buildSlicerOptionsRequestFilters(input.field, input.filters),
  }
}

export function toSlicerOptions(response: Raw): string[] {
  const options = new Set<string>()
  const rawOptions = response['options'] ?? response['Options']
  const values = Array.isArray(rawOptions) ? rawOptions : []
  values.forEach((value) => {
    if (value === null || value === undefined || value === '') return
    options.add(String(value))
  })

  return Array.from(options).sort((left, right) => left.localeCompare(right))
}

export function asBoolean(value: unknown) {
  return value === true || value === 'true' || value === 'True'
}

export function asIsoDateOrNull(value: unknown): string | null {
  const text = asOptionalString(value)?.trim()
  if (!text) return null
  // Accept "2026-09-29" as well as .NET DateTime/DateOnly serialisations ("2026-09-29T00:00:00").
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(text)
  return match?.[1] ?? null
}

function mapDatePresetOption(raw: Raw): DatePresetOption | null {
  const value = asString(getRawValue(raw, 'value', 'Value')).trim()
  if (!value) return null
  return {
    value,
    label: asString(getRawValue(raw, 'label', 'Label')).trim() || value,
    group: asString(getRawValue(raw, 'group', 'Group')).trim(),
    calendar: asOptionalString(getRawValue(raw, 'calendar', 'Calendar')) ?? null,
    from: asIsoDateOrNull(getRawValue(raw, 'from', 'From')),
    to: asIsoDateOrNull(getRawValue(raw, 'to', 'To')),
    isDefault: asBoolean(getRawValue(raw, 'isDefault', 'IsDefault')),
  }
}

export function mapDatePresetOptions(value: unknown): DatePresetOption[] {
  return asArray<unknown>(value)
    .map((item) => asObject(item))
    .filter((item): item is Raw => Boolean(item))
    .map(mapDatePresetOption)
    .filter((item): item is DatePresetOption => Boolean(item))
}
